import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  ArrowDown10, ArrowDownUp, ArrowUp10, BadgeCheck, BriefcaseBusiness, Building2, CalendarDays, ChevronDown, ChevronRight, ChevronUp, Clock3,
  ExternalLink, Filter, LocateFixed, LogOut, MapPin, MapPinned, RotateCcw, Search, ShieldCheck, SlidersHorizontal, UserRound, UserRoundCheck, UsersRound
} from 'lucide-react';
import { fetchAssignments, fetchDashboard, fetchOrganizations, fetchProfessionals, fetchReviews, searchProfessionals } from './api/resources';
import { useAuth } from './auth/AuthContext';
import { regionOptions, serviceOptions } from './catalog';
import { localizedApiError } from './i18n/apiErrors';
import { useI18n } from './i18n/I18nContext';
import { reviewCriterionEntries, reviewCriterionLabel } from './i18n/reviewCriteria';
import type { Locale, TranslationFunction, TranslationKey } from './i18n/translations';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import type { AppContext, Assignment, DashboardData, ExternalRequestMode, OrganizationSummary, Professional, Review } from './types';

type View = 'search' | 'assignments' | 'profiles' | 'organizations' | 'reviews';
type JobsTimeScope = 'today' | 'current' | 'past';
type OrganizationSortField = 'upcomingJobs' | 'completedJobs';
type SortDirection = 'asc' | 'desc';

type SearchOrigin = { id: string; label: string; detail: string; lat: number; lng: number };

const services = [
  { value: 'all', labelKey: 'service.all' },
  ...serviceOptions
] satisfies Array<{ value: string; labelKey: TranslationKey }>;

const serviceLabelKeys = new Map(services.map((service) => [service.value, service.labelKey]));
const externalModes = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'open', labelKey: 'externalMode.open' },
  { value: 'restricted', labelKey: 'externalMode.restricted' },
  { value: 'membership_only', labelKey: 'externalMode.membershipOnly' }
] satisfies Array<{ value: 'all' | ExternalRequestMode; labelKey: TranslationKey }>;

const modeLabelKeys: Record<ExternalRequestMode, TranslationKey> = {
  open: 'externalMode.open',
  restricted: 'externalMode.restricted',
  membership_only: 'externalMode.membershipOnly'
};

const statusLabelKeys: Record<Assignment['status'], TranslationKey> = {
  pending: 'assignment.status.pending',
  accepted: 'assignment.status.accepted.organization',
  declined: 'assignment.status.declined',
  completed: 'assignment.status.completed'
};

function assignmentStatusLabel(status: Assignment['status'], context: AppContext, t: TranslationFunction): string {
  if (status === 'accepted' && context.type === 'professional') return t('assignment.status.accepted.professional');
  return t(statusLabelKeys[status]);
}

const fallbackSearchOrigins = regionOptions satisfies Array<Omit<SearchOrigin, 'detail'> & { detailKey: TranslationKey }>;

function hasCoordinates(context: AppContext): context is AppContext & { lat: number; lng: number } {
  return typeof context.lat === 'number' && typeof context.lng === 'number';
}

function buildSearchOrigins(context: AppContext, browserOrigin: SearchOrigin | null, t: TranslationFunction): SearchOrigin[] {
  const contextOrigin = hasCoordinates(context)
    ? [{ id: 'context', label: context.label, detail: context.locationName ?? t('search.organizationLocation'), lat: context.lat, lng: context.lng }]
    : [];
  const origins = [
    ...contextOrigin,
    ...fallbackSearchOrigins.map(({ detailKey, ...origin }) => ({ ...origin, detail: t(detailKey) }))
  ];
  return browserOrigin ? [browserOrigin, ...origins.filter((origin) => origin.id !== browserOrigin.id)] : origins;
}

function getSearchCoordinates(origin: SearchOrigin) {
  return { lat: origin.lat, lng: origin.lng };
}

function contextIcon(type: AppContext['type'], size = 15) {
  if (type === 'organization') return <Building2 size={size} />;
  if (type === 'professional') return <UserRoundCheck size={size} />;
  return <UserRound size={size} />;
}

function displayServiceName(serviceName: string, t: TranslationFunction): string {
  const key = serviceLabelKeys.get(serviceName);
  return key ? t(key) : serviceName;
}

function displayServices(serviceNames: string[], t: TranslationFunction): string {
  return serviceNames.length > 0
    ? serviceNames.slice(0, 3).map((service) => displayServiceName(service, t)).join(', ')
    : t('common.missing');
}

function mapsUrlFor(context: AppContext): string | undefined {
  if (context.mapsUrl) return context.mapsUrl;
  if (hasCoordinates(context)) return `https://www.google.com/maps/search/?api=1&query=${context.lat},${context.lng}`;
  return undefined;
}

function optionalMapsUrlFor(item: { mapsUrl?: string | null }): string | undefined {
  return item.mapsUrl || undefined;
}

function useImageFallback(event: React.SyntheticEvent<HTMLImageElement>, fallbackUrl: string): void {
  if (event.currentTarget.dataset.fallbackApplied) return;
  event.currentTarget.dataset.fallbackApplied = 'true';
  event.currentTarget.src = fallbackUrl;
}

function formatTime(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function formatDate(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(value));
}

/** Lädt ausschliesslich Daten, die der Server für den aktiven Kontext freigibt. */
function useContextData(context: AppContext) {
  const { t } = useI18n();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [searchResults, setSearchResults] = useState<Professional[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function load(): Promise<void> {
      try {
        const defaultOrigin = buildSearchOrigins(context, null, t)[0];
        const searchRequest = context.type === 'organization' && defaultOrigin
          ? searchProfessionals(context, { radiusKm: 25, externalMode: 'all', ...getSearchCoordinates(defaultOrigin) })
          : Promise.resolve([]);
        const organizationRequest = context.type === 'professional' ? fetchOrganizations(context) : Promise.resolve([]);
        const [dashboardData, professionalData, searchData, organizationData, reviewData] = await Promise.all([
          fetchDashboard(context), fetchProfessionals(context), searchRequest, organizationRequest, fetchReviews(context)
        ]);
        if (!active) return;
        setDashboard(dashboardData);
        setProfessionals(professionalData);
        setSearchResults(searchData);
        setOrganizations(organizationData);
        setReviews(reviewData);
        setError(null);
      } catch (reason) {
        if (active) setError(localizedApiError(reason, t, 'error.dataLoad'));
      }
    }
    void load();
    return () => { active = false; };
  }, [context, t]);

  return { dashboard, professionals, searchResults, setSearchResults, organizations, reviews, error };
}

function SearchView({ context, results, onResults }: { context: AppContext; results: Professional[]; onResults: (items: Professional[]) => void }) {
  const { t } = useI18n();
  const [service, setService] = useState('all');
  const [mode, setMode] = useState<'all' | ExternalRequestMode>('all');
  const [radius, setRadius] = useState(15);
  const [browserOrigin, setBrowserOrigin] = useState<SearchOrigin | null>(null);
  const originOptions = useMemo(() => buildSearchOrigins(context, browserOrigin, t), [browserOrigin, context, t]);
  const [originId, setOriginId] = useState(originOptions[0]?.id ?? '');
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (!originOptions.some((origin) => origin.id === originId)) setOriginId(originOptions[0]?.id ?? '');
  }, [originId, originOptions]);

  const activeOrigin = originOptions.find((origin) => origin.id === originId) ?? originOptions[0];

  async function runSearch(nextService = service, nextMode = mode, nextRadius = radius, nextOrigin = activeOrigin): Promise<void> {
    if (!nextOrigin) return;
    setLoading(true);
    try {
      onResults(await searchProfessionals(context, {
        service: nextService, externalMode: nextMode, radiusKm: nextRadius, ...getSearchCoordinates(nextOrigin)
      }));
      setSearchError(null);
    } catch (reason) {
      setSearchError(localizedApiError(reason, t, 'error.search'));
    } finally { setLoading(false); }
  }

  function useCurrentPosition(): void {
    if (!navigator.geolocation) {
      setGeoError(t('search.locationUnavailable'));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((position) => {
      const nextOrigin = {
        id: 'current-position', label: t('search.currentPosition'), detail: t('search.browserLocation'),
        lat: position.coords.latitude, lng: position.coords.longitude
      };
      setBrowserOrigin(nextOrigin);
      setOriginId(nextOrigin.id);
      setGeoError(null);
      setLocating(false);
      void runSearch(service, mode, radius, nextOrigin);
    }, () => {
      setGeoError(t('search.locationFailed'));
      setLocating(false);
    }, { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout: 5000 });
  }

  return (
    <section className="view-stack">
      <div className="view-title-row"><h1>{t('common.search')}</h1><Search className="title-icon" size={24} /></div>
      <section className="filter-panel">
        <div className="filter-line"><Filter size={16} /><select value={service} onChange={(event) => { setService(event.target.value); void runSearch(event.target.value, mode, radius); }}>{services.map((entry) => <option key={entry.value} value={entry.value}>{t(entry.labelKey)}</option>)}</select></div>
        <div className="location-row">
          <label className="location-select"><span><MapPin size={16} /> {t('common.location')}</span><select value={originId} onChange={(event) => { const nextId = event.target.value; const nextOrigin = originOptions.find((origin) => origin.id === nextId); setOriginId(nextId); void runSearch(service, mode, radius, nextOrigin); }}>{originOptions.map((origin) => <option key={origin.id} value={origin.id}>{origin.label} · {origin.detail}</option>)}</select></label>
          <button className="icon-action" type="button" onClick={useCurrentPosition} title={t('search.useCurrentPosition')} aria-label={t('search.useCurrentPosition')}><LocateFixed size={18} /></button>
        </div>
        <div className="segmented" aria-label={t('search.externalFilter')}>{externalModes.map((entry) => <button type="button" key={entry.value} className={mode === entry.value ? 'active' : ''} onClick={() => { setMode(entry.value); void runSearch(service, entry.value, radius); }}>{t(entry.labelKey)}</button>)}</div>
        <label className="range-row"><span><MapPin size={16} /> {t('search.radius')}</span><input type="range" min="5" max="40" step="5" value={radius} onChange={(event) => { const next = Number(event.target.value); setRadius(next); void runSearch(service, mode, next); }} /><strong>{radius} km</strong></label>
        {geoError && <p className="hint-line">{geoError}</p>}
        {searchError && <p className="form-error" role="alert">{searchError}</p>}
      </section>
      <section className="list-stack">
        {(loading || locating) && <div className="loading-line">{t('search.running')}</div>}
        {!loading && !locating && !searchError && results.length === 0 && <div className="loading-line">{t('search.noResults')}</div>}
        {results.map((professional) => <ProfessionalCard key={professional.id} professional={professional} searchMode />)}
      </section>
    </section>
  );
}

function AssignmentCard({ assignment, context, compact = false, onOrganizationClick }: { assignment: Assignment; context: AppContext; compact?: boolean; onOrganizationClick?: (assignment: Assignment) => void }) {
  const { locale, t } = useI18n();
  return (
    <article className="item-card assignment-card">
      <div className="assignment-date-box"><Clock3 size={16} /><strong>{formatTime(assignment.startsAt, locale)}</strong><span>{formatDate(assignment.startsAt, locale)}</span></div>
      <div className="assignment-content">
        <div className="item-body">
          <div className="item-topline"><span className={`status-pill status-${assignment.status}`}>{assignmentStatusLabel(assignment.status, context, t)}</span><span className="source-pill">{t(assignment.source === 'external_request' ? 'assignment.source.external' : 'assignment.source.membership')}</span></div>
          <h3>{displayServiceName(assignment.serviceName, t)}</h3><p>{assignment.professionalName} · {context.type === 'professional' && onOrganizationClick ? <button type="button" className="text-link-button" onClick={() => onOrganizationClick(assignment)}>{assignment.organizationName}</button> : assignment.organizationName}</p>
          {!compact && assignment.note && <p className="muted-line">{assignment.note}</p>}
          <div className="customer-strip"><UsersRound size={15} />{assignment.customers.map((customer) => <span key={`${assignment.id}-${customer.customerName}`}>{customer.customerName}{customer.partySize > 1 ? ` (${customer.partySize})` : ''}</span>)}</div>
        </div><ChevronRight size={18} className="chevron" />
      </div>
    </article>
  );
}

function DashboardStatTile({ label, value, tone }: { label: string; value: number; tone?: 'warm' }) {
  return <div className={`stat-tile ${tone === 'warm' ? 'stat-tile-warm' : ''}`}><span>{label}</span><strong>{value}</strong></div>;
}

function OrganizationProfileCard({ context }: { context: AppContext }) {
  const { t } = useI18n();
  const mapsUrl = mapsUrlFor(context);
  return (
    <article className="item-card professional-card organization-profile-card">
      <div className="profile-main">
        {context.avatarUrl ? <img src={context.avatarUrl} alt="" className="organization-avatar avatar" onError={(event) => useImageFallback(event, '/placeholder-organization.svg')} /> : <div className="organization-icon avatar"><Building2 size={28} /></div>}
        <div className="item-body profile-heading organization-heading">
          <h3>{context.label}</h3>
          {(context.websiteUrl || mapsUrl) && <div className="organization-link-row profile-link-row heading-link-row">
            {context.websiteUrl && <a href={context.websiteUrl} target="_blank" rel="noreferrer" title={t('common.website')} aria-label={t('common.openWebsite')}><ExternalLink size={15} /></a>}
            {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" title={t('common.location')} aria-label={t('common.openLocation')}><MapPinned size={15} /></a>}
          </div>}
        </div>
      </div>
      <div className="profile-meta-block organization-meta-block">
        <div className="profile-meta-line"><BriefcaseBusiness size={14} /><span>{t('common.type')}</span><strong>{context.organizationType || t('common.missing')}</strong></div>
        <div className="profile-meta-line"><MapPin size={14} /><span>{t('common.locationRegion')}</span><strong>{context.locationName || t('common.missing')}</strong></div>
      </div>
    </article>
  );
}

function JobsProfileHeader({ dashboard, context, professionals }: { dashboard: DashboardData | null; context: AppContext; professionals: Professional[] }) {
  const { t } = useI18n();
  const ownProfessional = context.type === 'professional' ? professionals.find((professional) => professional.id === context.id) ?? professionals[0] : null;
  return (
    <section className="jobs-overview">
      {ownProfessional ? <ProfessionalCard professional={ownProfessional} /> : <OrganizationProfileCard context={context} />}
      <div className="stats-grid">
        <DashboardStatTile label={t(context.type === 'organization' ? 'common.professionals' : 'common.organizations')} value={context.type === 'organization' ? (dashboard?.stats.professionals ?? 0) : (dashboard?.stats.organizations ?? 0)} />
        <DashboardStatTile label={t('common.upcoming')} value={dashboard?.stats.upcomingJobs ?? 0} />
        <DashboardStatTile label={t('common.requests')} value={dashboard?.stats.pendingAssignments ?? 0} tone="warm" />
      </div>
    </section>
  );
}

function AssignmentsView({ context, dashboard, professionals, onOrganizationClick }: { context: AppContext; dashboard: DashboardData | null; professionals: Professional[]; onOrganizationClick?: (assignment: Assignment) => void }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<'active' | 'pending' | 'accepted' | 'all'>('active');
  const [timeScope, setTimeScope] = useState<JobsTimeScope>('today');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void fetchAssignments(context, { status, timeScope }).then((items) => { if (active) { setAssignments(items); setError(null); } })
      .catch((reason) => { if (active) setError(localizedApiError(reason, t, 'error.jobsLoad')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [context, status, t, timeScope]);

  const emptyText = timeScope === 'today'
    ? t('assignment.empty.today')
    : timeScope === 'current'
      ? t('assignment.empty.current')
      : t('assignment.empty.past');

  return (
    <section className="view-stack">
      <JobsProfileHeader dashboard={dashboard} context={context} professionals={professionals} />
      <div className="view-title-row"><h1>{t('common.jobs')}</h1><Clock3 className="title-icon" size={24} /></div>
      <section className="filter-panel assignment-filters">
        <div className="segmented three-options" aria-label={t('common.timeRange')}>
          <button type="button" className={timeScope === 'today' ? 'active' : ''} onClick={() => setTimeScope('today')}>{t('common.today')}</button>
          <button type="button" className={timeScope === 'current' ? 'active' : ''} onClick={() => setTimeScope('current')}>{t('common.upcoming')}</button>
          <button type="button" className={timeScope === 'past' ? 'active' : ''} onClick={() => setTimeScope('past')}>{t('common.past')}</button>
        </div>
        <div className="segmented four-options" aria-label={t('common.status')}>
          <button type="button" className={status === 'active' ? 'active' : ''} onClick={() => setStatus('active')}>{t('assignment.filter.active', { accepted: assignmentStatusLabel('accepted', context, t).toLowerCase() })}</button>
          <button type="button" className={status === 'pending' ? 'active' : ''} onClick={() => setStatus('pending')}>{t('common.pending')}</button>
          <button type="button" className={status === 'accepted' ? 'active' : ''} onClick={() => setStatus('accepted')}>{assignmentStatusLabel('accepted', context, t)}</button>
          <button type="button" className={status === 'all' ? 'active' : ''} onClick={() => setStatus('all')}>{t('assignment.filter.allStatuses')}</button>
        </div>
      </section>
      {error && <div className="error-banner">{error}</div>}
      {loading ? <div className="loading-line">{t('assignment.loading')}</div> : <div className="list-stack">{assignments.map((assignment) => <AssignmentCard key={assignment.id} assignment={assignment} context={context} onOrganizationClick={onOrganizationClick} />)}{assignments.length === 0 && <div className="loading-line">{emptyText}</div>}</div>}
    </section>
  );
}

function ProfessionalCard({ professional, searchMode = false }: { professional: Professional; searchMode?: boolean }) {
  const { locale, t } = useI18n();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const restricted = professional.externalRequestMode === 'restricted';
  const membershipNames = professional.memberships.map((membership) => membership.name).join(', ');
  const onlyNameAndOrganization = searchMode && restricted;
  const reviewAverageEntries = reviewCriterionEntries(professional.reviewAverages);
  const canShowReviewDetails = !onlyNameAndOrganization;
  return (
    <article className="item-card professional-card">
      <div className="profile-main">
        <img src={professional.avatarUrl ?? '/placeholder-avatar.svg'} alt="" className="avatar" onError={(event) => useImageFallback(event, '/placeholder-avatar.svg')} />
        <div className="item-body profile-heading">
          <h3>{professional.name}</h3>
          <div className="profile-title-row">
                      {(professional.websiteUrl || professional.mapsUrl) && <div className="organization-link-row profile-link-row heading-link-row">
            {professional.websiteUrl && <a href={professional.websiteUrl} target="_blank" rel="noreferrer" title={t('common.website')} aria-label={t('common.openWebsite')}><ExternalLink size={15} /></a>}
            {optionalMapsUrlFor(professional) && <a href={optionalMapsUrlFor(professional)} target="_blank" rel="noreferrer" title={t('common.location')} aria-label={t('common.openLocation')}><MapPinned size={15} /></a>}
          </div>}
            <p>{professional.title}</p><span className={`mode-pill mode-${professional.externalRequestMode}`}>{t(modeLabelKeys[professional.externalRequestMode])}</span>
          </div>

          
        </div>
      </div>
      <>
        <div className="profile-meta-block">
          <div className="profile-meta-line"><BriefcaseBusiness size={14} /><span>{t('common.services')}</span><strong>{displayServices(professional.services, t)}</strong></div>
          {membershipNames && <div className="profile-meta-line"><Building2 size={14} /><span>{t('common.organization')}</span><strong>{membershipNames}</strong></div>}
          <div className="profile-meta-line"><MapPin size={14} /><span>{t('common.locationRegion')}</span><strong>{professional.locationName || t('common.missing')} {professional.distanceKm !== undefined && <span className="distance profile-distance">{professional.distanceKm} km</span>}</strong></div>
        </div>
        <div className="profile-count-row">
          {canShowReviewDetails
            ? <button type="button" className={`count-chip reviews-row ${detailsOpen ? 'active' : ''}`} onClick={() => setDetailsOpen((open) => !open)} aria-expanded={detailsOpen}>
              <span>{t('common.reviews')}</span><strong>{professional.reviewCount}</strong>{detailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            : <div className="count-chip completed-services"><span>{t('common.reviews')}</span><strong>{professional.reviewCount}</strong></div>}
          <div className="count-chip completed-services"><span>{t('common.completedServices')}</span><strong>{professional.completedAssignmentCount}</strong></div>
        </div>
        {detailsOpen && canShowReviewDetails && <div className="profile-details">
          <section>
            <h4>{t('profile.reviewAverages')}</h4>
            {reviewAverageEntries.length > 0
              ? <div className="criteria-grid">{reviewAverageEntries.map(([code, value]) => <div key={code}><span>{reviewCriterionLabel(code, locale)}</span><strong>{value}/10</strong></div>)}</div>
              : <p className="muted-line">{t('profile.noReviews')}</p>}
          </section>
          <section>
            <h4>{t('common.qualifications')}</h4>
            {(professional.qualifications ?? []).length > 0
              ? <div className="tag-row qualification-tags">{(professional.qualifications ?? []).map((qualification) => <span key={qualification}>{qualification}</span>)}</div>
              : <p className="muted-line">{t('profile.noQualifications')}</p>}
          </section>
        </div>}
      </>
    </article>
  );
}

function ProfilesView({ professionals }: { professionals: Professional[] }) {
  const { t } = useI18n();
  return <section className="view-stack"><div className="view-title-row"><h1>{t('common.pros')}</h1><UserRoundCheck className="title-icon" size={24} /></div><div className="list-stack">{professionals.map((professional) => <ProfessionalCard key={professional.id} professional={professional} />)}</div></section>;
}

function OrganizationListCard({ organization }: { organization: OrganizationSummary }) {
  const { t } = useI18n();
  const mapsUrl = organization.mapsUrl || undefined;
  return (
    <article className="item-card professional-card organization-list-card">
      <div className="profile-main">
        {organization.avatarUrl ? <img src={organization.avatarUrl} alt="" className="organization-avatar avatar" onError={(event) => useImageFallback(event, '/placeholder-organization.svg')} /> : <div className="organization-icon avatar"><Building2 size={24} /></div>}
        <div className="item-body profile-heading organization-heading">
          <h3>{organization.name}</h3>
          {(organization.websiteUrl || mapsUrl) && <div className="organization-link-row profile-link-row heading-link-row">
            {organization.websiteUrl && <a href={organization.websiteUrl} target="_blank" rel="noreferrer" title={t('common.website')} aria-label={t('common.openWebsite')}><ExternalLink size={15} /></a>}
            {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" title={t('common.location')} aria-label={t('common.openLocation')}><MapPinned size={15} /></a>}
          </div>}
        </div>
      </div>
      <div className="profile-meta-block organization-meta-block">
        <div className="profile-meta-line"><BriefcaseBusiness size={14} /><span>{t('common.type')}</span><strong>{organization.type || t('common.missing')}</strong></div>
        <div className="profile-meta-line"><MapPin size={14} /><span>{t('common.locationRegion')}</span><strong>{organization.locationName || t('common.missing')}</strong></div>
        <div className="organization-count-row">
          <div className="profile-meta-line organization-count-item"><CalendarDays size={14} /><span>{t('common.upcoming')}</span><strong>{organization.upcomingJobs}</strong></div>
          <div className="profile-meta-line organization-count-item"><BadgeCheck size={14} /><span>{t('common.completed')}</span><strong>{organization.completedJobs}</strong></div>
        </div>
      </div>
    </article>
  );
}

function OrganizationsView({ organizations, query, onQueryChange }: { organizations: OrganizationSummary[]; query: string; onQueryChange: (query: string) => void }) {
  const { t } = useI18n();
  const [sortOpen, setSortOpen] = useState(false);
  const [sortField, setSortField] = useState<OrganizationSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOrganizations = useMemo(() => {
    const filtered = organizations.filter((organization) => {
      const haystack = `${organization.name} ${organization.type} ${organization.locationName}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
    if (!sortField) return filtered;
    return [...filtered].sort((left, right) => {
      const result = left[sortField] - right[sortField];
      return sortDirection === 'asc' ? result : -result;
    });
  }, [normalizedQuery, organizations, sortDirection, sortField]);

  function toggleSort(field: OrganizationSortField): void {
    if (sortField === field) {
      setSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortField(field);
    setSortDirection('desc');
  }

  function sortIcon(field: OrganizationSortField) {
    if (sortField !== field) return <ArrowDownUp size={15} />;
    return sortDirection === 'asc' ? <ArrowUp10 size={15} /> : <ArrowDown10 size={15} />;
  }

  return (
    <section className="view-stack">
      <div className="view-title-row"><h1>{t('common.organizations')}</h1><Building2 className="title-icon" size={24} /></div>
      <section className="filter-panel organizations-filter-panel">
        <label className="search-field"><Search size={16} /><input value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder={t('organization.searchPlaceholder')} /></label>
        <div className="organization-sort-panel">
          <button type="button" className={`icon-action sort-toggle ${sortOpen ? 'active' : ''}`} onClick={() => setSortOpen((open) => !open)} title={t('common.sorting')} aria-label={t('common.sorting')} aria-expanded={sortOpen}><SlidersHorizontal size={17} />{sortOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
          {sortOpen && <div className="organization-sort-actions" aria-label={t('organization.sortOptions')}>
            <button type="button" className={sortField === 'upcomingJobs' ? 'active' : ''} onClick={() => toggleSort('upcomingJobs')} title={t('organization.sortUpcoming')} aria-label={t('organization.sortUpcoming')}><CalendarDays size={16} />{sortIcon('upcomingJobs')}</button>
            <button type="button" className={sortField === 'completedJobs' ? 'active' : ''} onClick={() => toggleSort('completedJobs')} title={t('organization.sortCompleted')} aria-label={t('organization.sortCompleted')}><BadgeCheck size={16} />{sortIcon('completedJobs')}</button>
            <button type="button" onClick={() => { setSortField(null); setSortDirection('desc'); }} title={t('organization.resetSorting')} aria-label={t('organization.resetSorting')}><RotateCcw size={16} /></button>
          </div>}
        </div>
      </section>
      <div className="list-stack">{visibleOrganizations.map((organization) => <OrganizationListCard key={organization.id} organization={organization} />)}{visibleOrganizations.length === 0 && <div className="loading-line">{t('organization.noneFound')}</div>}</div>
    </section>
  );
}

function ReviewsView({ reviews }: { reviews: Review[] }) {
  const { locale, t } = useI18n();
  return (
    <section className="view-stack"><div className="view-title-row"><h1>{t('common.reviews')}</h1><ShieldCheck className="title-icon" size={24} /></div>
      <div className="list-stack">{reviews.map((review) => <article className="item-card review-card" key={review.id}><div className="item-body"><div className="item-topline"><span className="source-pill">{review.serviceName ? displayServiceName(review.serviceName, t) : t('review.link')}</span><span className="distance">{formatDate(review.createdAt, locale)}</span></div><h3>{review.professionalName}</h3><div className="criteria-grid">{reviewCriterionEntries(review.criteria).map(([code, value]) => <div key={code}><span>{reviewCriterionLabel(code, locale)}</span><strong>{value}/10</strong></div>)}</div>{review.textReview && <p className="review-text">{review.textReview}</p>}</div></article>)}</div>
    </section>
  );
}

function BottomNav({ view, setView, context }: { view: View; setView: (view: View) => void; context: AppContext }) {
  const { t } = useI18n();
  const items: Array<{ view: View; label: string; icon: React.ReactNode }> = [
    { view: 'assignments', label: t('common.jobs'), icon: <CalendarDays size={20} /> },
    ...(context.type === 'organization' ? [{ view: 'search' as View, label: t('common.search'), icon: <Search size={20} /> }] : []),
    ...(context.type === 'professional' ? [{ view: 'organizations' as View, label: t('common.organizations'), icon: <Building2 size={20} /> }] : []),
    ...(context.type !== 'professional' ? [{ view: 'profiles' as View, label: t('common.pros'), icon: <UserRoundCheck size={20} /> }] : []),
    { view: 'reviews', label: t('common.reviews'), icon: <SlidersHorizontal size={20} /> }
  ];
  return <nav className="bottom-nav" aria-label={t('navigation.main')} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>{items.map((item) => <button type="button" key={item.view} className={view === item.view ? 'active' : ''} onClick={() => setView(item.view)}>{item.icon}<span>{item.label}</span></button>)}</nav>;
}

function Shell() {
  const { activeContext } = useAuth();
  const { t } = useI18n();
  if (!activeContext) return <main className="loading-screen">{t('session.noContext')}</main>;
  return <ContextShell activeContext={activeContext} />;
}

function ContextShell({ activeContext }: { activeContext: AppContext }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [view, setView] = useState<View>('assignments');
  const [organizationQuery, setOrganizationQuery] = useState('');
  const data = useContextData(activeContext);

  useEffect(() => {
    if ((activeContext.type !== 'organization' && view === 'search') || (activeContext.type === 'professional' && view === 'profiles') || (activeContext.type !== 'professional' && view === 'organizations')) setView('assignments');
  }, [activeContext.type, view]);

  const currentView = useMemo(() => {
    if (view === 'search') return <SearchView context={activeContext} results={data.searchResults} onResults={data.setSearchResults} />;
    if (view === 'assignments') return <AssignmentsView context={activeContext} dashboard={data.dashboard} professionals={data.professionals} onOrganizationClick={(assignment) => { setOrganizationQuery(assignment.organizationName); setView('organizations'); }} />;
    if (view === 'profiles') return <ProfilesView professionals={data.professionals} />;
    if (view === 'organizations') return <OrganizationsView organizations={data.organizations} query={organizationQuery} onQueryChange={setOrganizationQuery} />;
    return <ReviewsView reviews={data.reviews} />;
  }, [activeContext, data.dashboard, data.organizations, data.professionals, data.reviews, data.searchResults, organizationQuery, view]);

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="context-control">
          <div className="context-row">
          <div className="context-chip">{contextIcon(activeContext.type)}<span>{activeContext.label}</span>
          </div>
          {activeContext.type === 'professional' && <select className="persona-select" aria-label={t('session.profileType')} value="pro" onChange={() => undefined}><option value="pro">{t('common.professionalShort')}</option><option value="customer" disabled>{t('common.customer')}</option></select>}
          </div>
        </div>
        <div className="session-actions"><span>{user?.displayName}</span>
          <button type="button" onClick={logout} title={t('common.logout')} aria-label={t('common.logout')}><LogOut size={18} /></button>
        </div>
      </header>
      {data.error && <div className="error-banner global-error">{data.error}</div>}
      <div className="content-frame">{currentView}</div>
      <BottomNav view={view} setView={setView} context={activeContext} />
    </main>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route element={<ProtectedRoute />}><Route path="/" element={<Shell />} /><Route path="*" element={<Navigate to="/" replace />} /></Route>
    </Routes>
  );
}
